// @flow
import * as React from 'react';
import {view, css, Divider, Table, TableCell, TableHeaderCell} from '@sail/ui';

const DATA = {
  typography: [
    {
      token: 'heading',
      usage: 'Used for labeling a section of your app',
    },
    {
      token: 'subheading',
      usage: 'Used for labeling content within a section of your app',
    },
    {
      token: 'body',
      usage: 'Primary body text of the app',
    },
    {
      token: 'caption',
      usage: 'Used for text that should be less prominent than body text',
    },
  ],
  colors: {
    background: [
      {
        token: 'surface',
        usage: 'Used for the background of apps',
        value: '#fff',
      },
      {
        token: 'container',
        usage: 'Used for cards and sections within an app',
        value: '#E0E6EB',
      },
    ],
    border: [
      {
        token: 'neutral',
        usage: 'The default color for all borders and dividers',
        value: '#E0E6EB',
      },
      {
        token: 'critical',
        usage: 'Used for content which is critically urgent to the user',
        value: '#df1b41',
      },
    ],
    text: [
      {
        token: 'primary',
        usage: 'The default color for text and icons',
        value: '#30313D',
      },
      {
        token: 'secondary',
        usage: 'Used to for text and icons which are less prominent',
        value: '#6A7383',
      },
      {
        token: 'disabled',
        usage: 'Used for elements which are disabled',
        value: '#A3ACBA',
      },
      {
        token: 'info',
        usage: 'Used for content that is neutral and informational',
        value: '#0570DE',
      },
      {
        token: 'success',
        usage: 'Used for content which indicates the success of some action',
        value: '#228403',
      },
      {
        token: 'attention',
        usage: 'Used for content which is should grab the user’s attention',
        value: '#C84801',
      },
      {
        token: 'critical',
        usage:
          'Used for content which is critically urgent to the user. Should be used sparingly.',
        value: '#DF1B41',
      },
    ],
  },
  textHandling: [
    {
      token:
        "{textOverflow: 'ellipsis', overflow: 'hidden', wordWrap: 'normal'}",
      usage:
        'Used for adding an ellipsis (...) to text that overflows the available space',
      css: {
        textOverflow: 'ellipsis',
        overflow: css.value('hidden'),
        wordWrap: 'normal',
        width: css.value('200px'),
      },
      text:
        'SupercalifragilisticexpialidociousSupercalifragilisticexpialidociousSupercalifragilisticexpialidocious',
    },
    {
      token: "{wordWrap: 'break-word'}",
      usage: 'Used for breaking up long words',
      css: {
        wordWrap: 'break-word',
        width: css.value('200px'),
      },
      text:
        'SupercalifragilisticexpialidociousSupercalifragilisticexpialidocious',
    },
    {
      token: "{whiteSpace: 'nowrap'}",
      usage: 'Used to prevent lines from wrapping',
      css: {
        whiteSpace: 'nowrap',
        overflow: 'visible',
        width: css.value('200px'),
        padding: 'small',
        keyline: 'neutral',
        marginRight: css.value('100px'),
        background: 'container',
      },
      text: 'This text is too long for the container.',
    },
    {
      token: "{textTransform: 'uppercase'}",
      usage: 'Used to uppercase text',
      css: {textTransform: 'uppercase'},
      text: 'Lorem ipsum dolor sit amet.',
    },
  ],
};

const Swatch = ({color}) => (
  <div
    style={{
      background: color,
      height: '32px',
      width: '32px',
      borderRadius: '4px',
      boxShadow: 'inset 0 0 1px #A3ACBA',
    }}
  />
);

const TypeExample = ({font}: {font: string}) => (
  <view.span css={{font}}>
    The quick brown fox jumps over the lazy dog.
  </view.span>
);

const ColorTable = ({category}: {category: string}) => {
  const items = DATA.colors[category];

  return (
    <Table fixed type="striped">
      <thead>
        <tr>
          <TableHeaderCell>Example</TableHeaderCell>
          <TableHeaderCell>Token</TableHeaderCell>
          <TableHeaderCell>Usage</TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.token}>
            <TableCell>
              <Swatch color={item.value} />
            </TableCell>
            <TableCell>
              <code className="InlineCode" style={{userSelect: 'all'}}>
                {item.token}
              </code>
            </TableCell>
            <TableCell>{item.usage}</TableCell>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const TypographyTable = () => {
  const items = DATA.typography;

  return (
    <Table fixed type="striped">
      <thead>
        <tr>
          <TableHeaderCell>Token</TableHeaderCell>
          <TableHeaderCell>Usage</TableHeaderCell>
          <TableHeaderCell>Example</TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.token}>
            <TableCell>
              <view.span css={{fontFamily: 'monospace'}}>
                {item.token}
              </view.span>
            </TableCell>
            <TableCell>{item.usage}</TableCell>
            <TableCell>
              <TypeExample font={item.token} />
            </TableCell>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const TextHandlingTable = () => {
  const items = DATA.textHandling;

  return (
    <Table fixed type="striped">
      <thead>
        <tr>
          <TableHeaderCell>CSS</TableHeaderCell>
          <TableHeaderCell>Usage</TableHeaderCell>
          <TableHeaderCell>Example</TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.token}>
            <TableCell>
              <view.span css={{fontFamily: 'monospace'}}>
                {item.token}
              </view.span>
            </TableCell>
            <TableCell>{item.usage}</TableCell>
            <TableCell>
              <view.div css={item.css}>{item.text}</view.div>
            </TableCell>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

type TailorFrameworkExampleProps = {|
  children: React.Node,
  type: string,
  args: any,
|};

const ExampleBox = (props: {css?: Object}) => (
  <view.div
    css={{
      background: 'surface',
      keyline: css.value('#9a9fb1'),
      padding: 'xlarge',
      ...(props.css ? props.css : {}),
    }}
  />
);

export const TailorFrameworkExample = ({
  type,
  args,
  children,
}: TailorFrameworkExampleProps) => {
  if (type === 'layout') {
    const layoutContainerCss = {
      keyline: 'neutral',
      padding: 'medium',
      marginTop: 'medium',
      background: 'container',
    };
    switch (args.example) {
      case 'horizontal-stretch':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
            }}
          >
            <ExampleBox />
            <ExampleBox />
            <ExampleBox />
          </view.div>
        );
      case 'horizontal-fractional-width':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
            }}
          >
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '3/4'}} />
          </view.div>
        );
      case 'horizontal-single-fractional-width':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
            }}
          >
            <ExampleBox />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox />
          </view.div>
        );
      case 'horizontal-start':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
              alignX: 'start',
            }}
          >
            <ExampleBox />
            <ExampleBox />
            <ExampleBox />
          </view.div>
        );
      case 'horizontal-distribute':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              distribute: 'space-between',
            }}
          >
            <ExampleBox />
            <ExampleBox />
            <ExampleBox />
          </view.div>
        );
      case 'horizontal-end':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
              alignX: 'end',
            }}
          >
            <ExampleBox />
            <ExampleBox />
            <ExampleBox />
          </view.div>
        );
      case 'horizontal-vertical-bottom':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
              alignX: 'start',
              alignY: 'bottom',
            }}
          >
            <ExampleBox css={{paddingY: 'xlarge'}} />
            <ExampleBox css={{paddingY: 'xxlarge'}} />
            <ExampleBox css={{paddingY: 'large'}} />
          </view.div>
        );
      case 'divider':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'small',
              alignX: 'start',
            }}
          >
            <ExampleBox />
            <Divider />
            <ExampleBox />
            <Divider />
            <ExampleBox />
          </view.div>
        );
      case 'wrapping':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
              wrap: 'wrap',
              alignX: 'start',
            }}
          >
            <ExampleBox css={{width: '1/2'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/3'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
          </view.div>
        );
      case 'gap-x-y':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gapX: 'small',
              gapY: 'large',
              wrap: 'wrap',
              alignX: 'start',
            }}
          >
            <ExampleBox css={{width: '1/2'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/3'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
          </view.div>
        );
      case 'wrap-horizontal-center':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
              wrap: 'wrap',
              alignX: 'center',
            }}
          >
            <ExampleBox css={{width: '1/2'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/3'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
          </view.div>
        );
      case 'wrap-vertical-center':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'x',
              gap: 'medium',
              wrap: 'wrap',
              alignX: 'start',
              alignY: 'center',
            }}
          >
            <ExampleBox css={{width: '1/2', paddingY: 'small'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/3'}} />
            <ExampleBox css={{width: '1/4', paddingY: 'medium'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '1/4', paddingY: 'small'}} />
          </view.div>
        );
      case 'vertical-stretch':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'y',
              gap: 'medium',
            }}
          >
            <ExampleBox />
            <ExampleBox />
            <ExampleBox />
          </view.div>
        );
      case 'vertical-horizontally-centered':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'y',
              gap: 'medium',
              alignX: 'center',
            }}
          >
            <ExampleBox css={{width: '1/4'}} />
            <ExampleBox css={{width: '2/3'}} />
            <ExampleBox css={{width: '1/3'}} />
          </view.div>
        );
      case 'layered':
        return (
          <view.div
            css={{
              ...layoutContainerCss,
              stack: 'z',
              alignX: 'center',
              alignY: 'center',
            }}
          >
            <ExampleBox css={{padding: 'xxlarge'}} />
            <ExampleBox css={{padding: 'large'}} />
            <ExampleBox css={{padding: 'small'}} />
          </view.div>
        );
      default:
        return null;
    }
  }

  if (type === 'color') {
    return <ColorTable category={args.category} />;
  }

  if (type === 'typography') {
    return <TypographyTable />;
  }

  if (type === 'textHandling') {
    return <TextHandlingTable />;
  }

  return null;
};
